import {
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  Stack,
} from '@mui/material';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import {
  useBinarys,
  useDocumentReferences,
  useOrganizations,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { useCallback, useState } from 'react';
import { createBinaryCustom, saveDocumentReference } from 'src/careflow/common/document/document';
import Viewer from 'src/careflow/care-coordination/patient/assessment/viewer/Viewer';
import { UploadFile } from 'src/careflow/common/document/UploadFile';
import CancelButton from 'src/careflow/common/CancelButton';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { OrganizationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { useSnackbar } from 'notistack';

type Props = {
  open: boolean;
  setOpen: Function;
  patient: WrappedPatient | null;
  task: WrappedTask | undefined;
  i18n: any;
  configurations: any;
  updateState: Function;
};

export function TaskDocument({
  open,
  setOpen,
  patient,
  task,
  i18n,
  configurations,
  updateState,
}: Props) {
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [fileUp, setFileUp] = useState<any>('');
  const [file, setFile] = useState<any>('');

  const authUser = useAuth().getCurrentUser();
  const { enqueueSnackbar } = useSnackbar();

  const [, { create: createBinary }] = useBinarys({ autofetch: false });
  const [, { create: createDocumentReference }] = useDocumentReferences({
    autofetch: false,
  });
  const [organizations] = useOrganizations({ filter: { active: true }, map: OrganizationWrapper });

  const handleDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setFile(
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );
      setFileUp(file);
    }
  }, []);

  const onSubmit = async () => {
    try {
      setOpenBackdrop(true);

      const resultBinary = await createBinaryCustom(
        {
          file: fileUp,
          fileType: fileUp.type,
          name: fileUp.name,
        },
        patient,
        createBinary
      );

      const mapDocumentToSave: any = saveDocumentReference(
        resultBinary,
        null,
        authUser,
        organizations,
        null,
        null,
        patient,
        task,
        configurations
      );

      const documentSaved = await createDocumentReference(mapDocumentToSave);

      updateState({
        reference: `DocumentReference/${documentSaved?.[0]?.id}`,
        type: 'DocumentReference',
      });

      enqueueSnackbar('Attachment was created');
      onClose();
    } catch (error) {
      enqueueSnackbar('Attachment was not created', { variant: 'error' });
      onClose();
    }
  };

  const onClose = () => {
    setFileUp('');
    setFile('');
    setOpen(false);
    setOpenBackdrop(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') onClose();
      }}
    >
      <DialogTitle>{i18n('patients.details.attachments.button', 'crs')}</DialogTitle>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {file === '' ? (
        <UploadFile onDrop={handleDrop} />
      ) : (
        <Stack sx={{ m: 1 }}>{file && <Viewer file={file} typeResource="blob" />}</Stack>
      )}
      <Stack spacing={2} alignItems="center">
        <DialogActions>
          <CancelButton onClick={onClose}>{i18n('cancel')}</CancelButton>

          <Button
            variant="contained"
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onSubmit();
            }}
          >
            {i18n('saveChanges')}
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
}
